import { useState, useEffect, useCallback, useMemo } from "react";
import axios, { AxiosInstance, AxiosResponse, AxiosError, CancelTokenSource, AxiosHeaders } from "axios";

interface FetchDataParams {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  data?: any;
  params?: any;
}

const useAxios = (baseURL: string, token?: string) => {
  const [response, setResponse] = useState<any | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const axiosInstance: AxiosInstance = useMemo(() => {
    const instance = axios.create({ baseURL });

    instance.interceptors.request.use((config) => {
      if (token) {
        if (!config.headers) {
          config.headers = new AxiosHeaders();
        }
        config.headers.set("Authorization", `Bearer ${token}`);
      }
      return config;
    });

    return instance;
  }, [baseURL, token]);

  const fetchData = useCallback(
    async ({ url, method, data = {}, params = {} }: FetchDataParams) => {
      setLoading(true);
      const source: CancelTokenSource = axios.CancelToken.source();

      try {
        const result = await axiosInstance({
          url,
          method,
          data: method.toLowerCase() === "get" ? undefined : data,
          params: method.toLowerCase() === "get" ? params : undefined,
          cancelToken: source.token,
        });
        setResponse(result.data);
        setError("");
      } catch (err: any) {
        if (axios.isCancel(err)) {
          console.warn("Request cancelled:", err.message);
        } else {
          setError(err.response?.data || err.message);
        }
      } finally {
        setLoading(false);
      }

      return () => {
        source.cancel("Request cancelled due to component unmounting.");
      };
    },
    [axiosInstance]
  );

  return { response, error, loading, fetchData };
};

export default useAxios;
